/**
 * Transaction Error Message Mapping
 * 
 * Maps technical Firestore transaction error codes to user-friendly messages.
 * Provides clear, actionable feedback for end users without exposing
 * technical details.
 * 
 * @module transaction-error-messages
 */

import { TransactionError } from './transaction-utils';

/**
 * Error codes that indicate transaction conflicts
 */
const CONFLICT_CODES = ['ABORTED', 'FAILED_PRECONDITION'];

/**
 * Error codes that indicate network issues
 */
const NETWORK_CODES = ['UNAVAILABLE', 'DEADLINE_EXCEEDED'];

/**
 * User-friendly error messages by category
 */
const ERROR_MESSAGES = {
  conflict: "Your change couldn't be saved because someone else updated the board. Please try again.",
  network: 'Network connection issue. Your change will be saved when connection is restored.',
  permission: "You don't have permission to make this change. Contact your administrator.",
  notFound: 'The item you tried to update no longer exists. The board will refresh.',
  unknown: 'Something went wrong. The board will refresh automatically.',
} as const;

/**
 * Get a user-friendly error message from a transaction error
 * 
 * Maps Firestore error codes to appropriate user messages:
 * - ABORTED / FAILED_PRECONDITION → Conflict message (someone else edited)
 * - UNAVAILABLE / DEADLINE_EXCEEDED → Network issue message
 * - PERMISSION_DENIED → Permission denied message
 * - NOT_FOUND → Item not found message
 * - Default → Generic error message
 * 
 * @param error The TransactionError from a failed transaction
 * @returns User-friendly error message string
 * 
 * @example
 * ```typescript
 * try {
 *   await transactionBoardSvc.saveBoard(...);
 * } catch (error) {
 *   const userMessage = getUserFriendlyMessage(error as TransactionError);
 *   notificationService.showError(userMessage);
 * }
 * ```
 */
export function getUserFriendlyMessage(error: TransactionError | Error | any): string {
  // Handle null/undefined errors
  if (!error) {
    return ERROR_MESSAGES.unknown;
  }

  // Extract error code - handle both TransactionError and regular errors
  const code = error?.code || '';
  const message = error?.message || '';
  
  // Check for conflict errors
  if (CONFLICT_CODES.some(c => code.includes(c) || message.includes(c))) {
    return ERROR_MESSAGES.conflict;
  }
  
  // Check for network errors
  if (NETWORK_CODES.some(c => code.includes(c) || message.includes(c))) {
    return ERROR_MESSAGES.network;
  }
  
  // Check for permission denied
  if (code.includes('PERMISSION_DENIED') || message.includes('PERMISSION_DENIED')) {
    return ERROR_MESSAGES.permission;
  }
  
  // Check for not found
  if (code.includes('NOT_FOUND') || message.includes('NOT_FOUND') || message.includes('not found')) {
    return ERROR_MESSAGES.notFound;
  }
  
  // Default to generic message
  return ERROR_MESSAGES.unknown;
}

/**
 * Check if an error is a transaction conflict that the user can retry
 * 
 * @param error The error to check
 * @returns true if the user should be prompted to retry
 */
export function isUserRetryableError(error: TransactionError | Error | any): boolean {
  if (!error) return false;
  
  const code = error?.code || '';
  const message = error?.message || '';
  
  return CONFLICT_CODES.some(c => code.includes(c) || message.includes(c));
}

/**
 * Check if an error is a network issue that may resolve on its own
 * 
 * @param error The error to check
 * @returns true if the error is network-related
 */
export function isNetworkError(error: TransactionError | Error | any): boolean {
  if (!error) return false;
  
  const code = error?.code || '';
  const message = error?.message || '';
  
  return NETWORK_CODES.some(c => code.includes(c) || message.includes(c));
}
